import { useEffect, useMemo, useState } from "react";
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetFooter,
  SheetHeader,
  SheetTitle,
} from "@/components/ui/sheet";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Badge } from "@/components/ui/badge";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";
import { Loader2, Search, X } from "lucide-react";
import type { DiscountRow } from "@/components/admin/DiscountFormSheet";

interface VipLink {
  id: string;
  user_id: string;
  discount_id: string;
  notes: string | null;
  created_at: string;
  profile?: { email: string; first_name: string | null; last_name: string | null } | null;
  discount?: Pick<DiscountRow, "id" | "name" | "code" | "discount_type" | "value"> | null;
}

interface ProfileLite {
  id: string;
  email: string;
  first_name: string | null;
  last_name: string | null;
}

interface Props {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** When set, the sheet works on this customer only (picker hidden). */
  presetUser?: ProfileLite | null;
}

const VipDiscountSheet = ({ open, onOpenChange, presetUser }: Props) => {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [links, setLinks] = useState<VipLink[]>([]);
  const [discounts, setDiscounts] = useState<DiscountRow[]>([]);

  const [search, setSearch] = useState("");
  const [matches, setMatches] = useState<ProfileLite[]>([]);
  const [searching, setSearching] = useState(false);
  const [selectedUser, setSelectedUser] = useState<ProfileLite | null>(null);
  const [selectedDiscountId, setSelectedDiscountId] = useState<string>("");
  const [notes, setNotes] = useState("");

  const fetchAll = async () => {
    setLoading(true);
    const [linksRes, discountsRes] = await Promise.all([
      (supabase.from as any)("customer_discounts")
        .select("id, user_id, discount_id, notes, created_at, discounts(id, name, code, discount_type, value)")
        .order("created_at", { ascending: false }),
      (supabase.from as any)("discounts")
        .select("*")
        .eq("active", true)
        .order("name"),
    ]);

    if (linksRes.error) {
      toast.error("Kunde inte hämta VIP-rabatter");
      console.error(linksRes.error);
    } else {
      const rows = (linksRes.data ?? []) as any[];
      const userIds = Array.from(new Set(rows.map((r) => r.user_id)));
      let profiles: Record<string, ProfileLite> = {};
      if (userIds.length > 0) {
        const { data: profs } = await supabase
          .from("profiles")
          .select("id, email, first_name, last_name")
          .in("id", userIds);
        if (profs) {
          for (const p of profs) profiles[p.id] = p as ProfileLite;
        }
      }
      setLinks(
        rows.map((r) => ({
          id: r.id,
          user_id: r.user_id,
          discount_id: r.discount_id,
          notes: r.notes,
          created_at: r.created_at,
          discount: r.discounts ?? null,
          profile: profiles[r.user_id] ?? null,
        })),
      );
    }

    if (discountsRes.error) {
      console.error(discountsRes.error);
    } else {
      setDiscounts(((discountsRes.data ?? []) as DiscountRow[]).filter((d) => d.active));
    }
    setLoading(false);
  };

  useEffect(() => {
    if (open) fetchAll();
  }, [open]);

  useEffect(() => {
    if (open && presetUser) {
      setSelectedUser(presetUser);
      setSearch("");
      setMatches([]);
    }
  }, [open, presetUser]);

  useEffect(() => {
    if (!search.trim()) {
      setMatches([]);
      return;
    }
    setSearching(true);
    const t = setTimeout(async () => {
      const q = search.trim();
      const { data, error } = await supabase
        .from("profiles")
        .select("id, email, first_name, last_name")
        .or(`email.ilike.%${q}%,first_name.ilike.%${q}%,last_name.ilike.%${q}%`)
        .limit(8);
      setSearching(false);
      if (error) {
        console.error(error);
        return;
      }
      setMatches((data ?? []) as ProfileLite[]);
    }, 250);
    return () => clearTimeout(t);
  }, [search]);

  const fmtDiscount = (d: VipLink["discount"]) => {
    if (!d) return "—";
    const value = d.discount_type === "percent"
      ? `−${d.value}%`
      : `−${(d.value / 100).toFixed(0)} kr`;
    return `${d.name} (${value})`;
  };

  const handleAssign = async () => {
    if (!selectedUser) return toast.error("Välj en kund");
    if (!selectedDiscountId) return toast.error("Välj en rabatt");

    setSaving(true);
    const { error } = await (supabase.from as any)("customer_discounts").insert({
      user_id: selectedUser.id,
      discount_id: selectedDiscountId,
      notes: notes.trim() || null,
    });
    setSaving(false);
    if (error) {
      toast.error(error.message || "Kunde inte koppla rabatt");
      return;
    }
    toast.success("VIP-rabatt kopplad");
    setSelectedUser(null);
    setSelectedDiscountId("");
    setNotes("");
    setSearch("");
    fetchAll();
  };

  const handleRemove = async (link: VipLink) => {
    if (!confirm(`Ta bort VIP-rabatt för ${link.profile?.email ?? "kund"}?`)) return;
    const { error } = await (supabase.from as any)("customer_discounts").delete().eq("id", link.id);
    if (error) {
      toast.error("Kunde inte ta bort");
      return;
    }
    toast.success("VIP-rabatt borttagen");
    fetchAll();
  };

  const availableDiscounts = useMemo(
    () => discounts.filter((d) => !d.code),
    [discounts],
  );

  const visibleLinks = useMemo(
    () => (presetUser ? links.filter((l) => l.user_id === presetUser.id) : links),
    [links, presetUser],
  );

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent className="w-full sm:max-w-2xl overflow-y-auto">
        <SheetHeader>
          <SheetTitle>VIP-priser</SheetTitle>
          <SheetDescription>
            Koppla en automatisk rabatt till en specifik kund. Rabatten appliceras
            utan att kunden behöver skriva in en kod. Endast rabatter <em>utan</em> kod
            visas — använd "Ny rabatt" och lämna kodfältet tomt för att skapa en VIP-rabatt.
          </SheetDescription>
        </SheetHeader>

        <div className="space-y-6 py-6">
          <div className="rounded-lg border p-4 space-y-3 bg-muted/20">
            <h3 className="text-sm font-semibold">Ny koppling</h3>

            {presetUser ? (
              <div>
                <Label>Kund</Label>
                <div className="mt-1 rounded-md border bg-background px-3 py-2 text-sm">
                  {presetUser.email}
                </div>
              </div>
            ) : (
            <div>
              <Label>Sök kund (e-post eller namn)</Label>
              <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                <Input
                  className="pl-9"
                  value={selectedUser ? `${selectedUser.email}` : search}
                  onChange={(e) => {
                    setSelectedUser(null);
                    setSearch(e.target.value);
                  }}
                  placeholder="kund@example.com"
                />
                {selectedUser && (
                  <button
                    type="button"
                    onClick={() => setSelectedUser(null)}
                    className="absolute right-2 top-1/2 -translate-y-1/2 p-1 hover:bg-muted rounded"
                  >
                    <X className="h-3.5 w-3.5" />
                  </button>
                )}
              </div>
              {!selectedUser && search && (
                <div className="mt-1 border rounded-md max-h-48 overflow-y-auto bg-background">
                  {searching ? (
                    <div className="p-2 text-xs text-muted-foreground">
                      <Loader2 className="h-3 w-3 inline animate-spin mr-1" /> Söker...
                    </div>
                  ) : matches.length === 0 ? (
                    <div className="p-2 text-xs text-muted-foreground">Inga träffar</div>
                  ) : (
                    matches.map((m) => (
                      <button
                        key={m.id}
                        type="button"
                        onClick={() => {
                          setSelectedUser(m);
                          setSearch("");
                          setMatches([]);
                        }}
                        className="w-full text-left px-3 py-2 text-sm hover:bg-muted border-b last:border-b-0"
                      >
                        <div className="font-medium">{m.email}</div>
                        {(m.first_name || m.last_name) && (
                          <div className="text-xs text-muted-foreground">
                            {m.first_name} {m.last_name}
                          </div>
                        )}
                      </button>
                    ))
                  )}
                </div>
              )}
            </div>
            )}

            <div>
              <Label>Rabatt</Label>
              <Select value={selectedDiscountId} onValueChange={setSelectedDiscountId}>
                <SelectTrigger>
                  <SelectValue placeholder="Välj VIP-rabatt..." />
                </SelectTrigger>
                <SelectContent>
                  {availableDiscounts.length === 0 ? (
                    <div className="px-3 py-2 text-xs text-muted-foreground">
                      Inga aktiva VIP-rabatter. Skapa en utan kod först.
                    </div>
                  ) : (
                    availableDiscounts.map((d) => (
                      <SelectItem key={d.id} value={d.id}>
                        {d.name} —{" "}
                        {d.discount_type === "percent"
                          ? `${d.value}%`
                          : `${(d.value / 100).toFixed(0)} kr`}
                      </SelectItem>
                    ))
                  )}
                </SelectContent>
              </Select>
            </div>

            <div>
              <Label>Anteckning (intern)</Label>
              <Textarea
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
                rows={2}
                placeholder="T.ex. långvarig storkund, partnerrabatt..."
              />
            </div>

            <Button
              onClick={handleAssign}
              disabled={saving || !selectedUser || !selectedDiscountId}
              className="w-full"
            >
              {saving ? "Sparar..." : "Koppla VIP-rabatt"}
            </Button>
          </div>

          <div>
            <h3 className="text-sm font-semibold mb-2">
              Aktiva kopplingar {!loading && <Badge variant="secondary">{visibleLinks.length}</Badge>}
            </h3>
            {loading ? (
              <div className="text-xs text-muted-foreground">Laddar...</div>
            ) : visibleLinks.length === 0 ? (
              <div className="text-xs text-muted-foreground italic p-3">
                Inga VIP-kunder än
              </div>
            ) : (
              <div className="space-y-2">
                {visibleLinks.map((link) => (
                  <div
                    key={link.id}
                    className="flex items-start justify-between gap-3 p-3 border rounded-md text-sm"
                  >
                    <div className="flex-1 min-w-0">
                      <div className="font-medium truncate">
                        {link.profile?.email ?? "(okänd kund)"}
                      </div>
                      {(link.profile?.first_name || link.profile?.last_name) && (
                        <div className="text-xs text-muted-foreground">
                          {link.profile.first_name} {link.profile.last_name}
                        </div>
                      )}
                      <div className="text-xs mt-1">
                        <span className="text-muted-foreground">Rabatt: </span>
                        <span className="font-medium">{fmtDiscount(link.discount)}</span>
                      </div>
                      {link.notes && (
                        <div className="text-xs italic text-muted-foreground mt-1">
                          {link.notes}
                        </div>
                      )}
                    </div>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => handleRemove(link)}
                    >
                      <X className="h-4 w-4" />
                    </Button>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>

        <SheetFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            Stäng
          </Button>
        </SheetFooter>
      </SheetContent>
    </Sheet>
  );
};

export default VipDiscountSheet;
